import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
// Layout follows Skool's classroom (Dew, 2026-10-26): title + progress pill, folded sections, check on the right.
import type { Course, Lesson, Progress } from "../lib/firebase";

type Props = {
  course: Course;
  lessons: Lesson[];
  progress: Map<string, Progress>;
  activeLessonId: string | null;
  /** Admin-only controls. Move/drag indexes into `lessons`; `section` = dropped on that section's header. */
  admin?: {
    onAdd: () => void;
    onMove: (from: number, to: number, section?: string) => void;
    onDelete: (lessonIds: string[]) => void;
    /** Rename in place from the sidebar (Dew, 2026-10-27): a lesson title, or the sectionTitle of the given lessons. */
    onRename: (lessonIds: string[], patch: { title: string } | { sectionTitle: string }) => void;
  };
};

/** Ask for a new name; null when cancelled, blank or unchanged. */
function askName(label: string, current: string): string | null {
  const next = window.prompt(label, current)?.trim();
  return next && next !== current ? next : null;
}

type Section = {
  key: string;
  title: string | null;
  lessons: Lesson[];
};

function groupLessons(lessons: Lesson[]): Section[] {
  const sections: Section[] = [];
  let currentKey: string | null = null;
  for (const l of lessons) {
    const key = l.sectionTitle?.trim() || null;
    if (sections.length === 0 || key !== currentKey) {
      sections.push({ key: key ?? `__no-section-${sections.length}`, title: key, lessons: [] });
      currentKey = key;
    }
    sections[sections.length - 1].lessons.push(l);
  }
  return sections;
}

export function CourseSidebar({ course, lessons, progress, activeLessonId, admin }: Props) {
  const sections = useMemo(() => groupLessons(lessons), [lessons]);
  const completedCount = lessons.filter((l) => progress.get(l.id)?.completedAt).length;
  const percent = lessons.length === 0 ? 0 : Math.round((completedCount / lessons.length) * 100);

  // Skool-style: only the section holding the active lesson starts open (admins too).
  const activeSectionKey = sections.find((s) => s.lessons.some((l) => l.id === activeLessonId))?.key;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Admin drag & drop: index (into `lessons`) of the lesson being dragged.
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const dropProps = (to: number, section?: string) =>
    admin && dragFrom !== null
      ? {
          onDragOver: (e: React.DragEvent) => e.preventDefault(),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            if (dragFrom !== to || section !== undefined) admin.onMove(dragFrom, to, section);
            setDragFrom(null);
          },
        }
      : {};

  return (
    <aside className="course-sidebar">
      <div className="course-sidebar__header">
        <h2 className="course-sidebar__title">{course.title}</h2>
        <div
          className="course-progress-pill"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          title={`เรียนแล้ว ${completedCount}/${lessons.length} บท`}
        >
          <div className="course-progress-pill__fill" style={{ width: `max(${percent}%, 2.75rem)` }}>
            {percent}%
          </div>
        </div>
      </div>

      <nav className="course-sidebar__nav">
        {sections.map((section) => {
          const isActive = section.key === activeSectionKey;
          // Admins drop onto a folded section's header to move a lesson into it.
          const isCollapsed = collapsed[section.key] ?? (!isActive && !!section.title);
          const last = lessons.indexOf(section.lessons[section.lessons.length - 1]);

          return (
            <section
              key={section.key}
              className={`curriculum-section ${isActive ? "curriculum-section--active" : ""}`}
            >
              {section.title && (
                <div
                  className="curriculum-section__head"
                  {...dropProps(dragFrom !== null && dragFrom <= last ? last : last + 1, section.title)}
                >
                <button
                  type="button"
                  className="curriculum-section__header"
                  onClick={() => setCollapsed((c) => ({ ...c, [section.key]: !isCollapsed }))}
                  aria-expanded={!isCollapsed}
                >
                  <span className="curriculum-section__title">{section.title}</span>
                  <span className={`curriculum-section__chevron ${isCollapsed ? "" : "is-open"}`} aria-hidden>
                    <svg viewBox="0 0 16 16" width="16" height="16"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                </button>
                {admin && (
                  <button
                    type="button"
                    className="curriculum-delete"
                    aria-label={`เปลี่ยนชื่อหมวด ${section.title}`}
                    title="เปลี่ยนชื่อหมวด"
                    onClick={() => {
                      const name = askName("ชื่อหมวดใหม่", section.title!);
                      if (name) admin.onRename(section.lessons.map((l) => l.id), { sectionTitle: name });
                    }}
                  >
                    ✏️
                  </button>
                )}
                {admin && (
                  <button
                    type="button"
                    className="curriculum-delete"
                    aria-label={`ลบหมวด ${section.title}`}
                    title="ลบหมวดนี้และทุกบทข้างใน"
                    onClick={() => {
                      // A whole section is many lessons in one click, so this one asks.
                      if (confirm(`ลบหมวด "${section.title}" และ ${section.lessons.length} บทข้างใน?`))
                        admin.onDelete(section.lessons.map((l) => l.id));
                    }}
                  >
                    🗑
                  </button>
                )}
                </div>
              )}
              {!isCollapsed && (
                <ul className="curriculum-lessons">
                  {section.lessons.map((l) => {
                    const isDone = !!progress.get(l.id)?.completedAt;
                    const isHere = l.id === activeLessonId;
                    const at = lessons.indexOf(l);
                    return (
                      <li
                        key={l.id}
                        className={`${isHere ? "is-active" : ""} ${dragFrom === at ? "is-dragging" : ""}`}
                        draggable={!!admin}
                        onDragStart={admin ? () => setDragFrom(at) : undefined}
                        onDragEnd={admin ? () => setDragFrom(null) : undefined}
                        {...dropProps(at)}
                      >
                        {admin && (
                          <span className="curriculum-lesson__move">
                            <button type="button" aria-label="เลื่อนขึ้น" disabled={at === 0} onClick={() => admin.onMove(at, at - 1)}>▲</button>
                            <button type="button" aria-label="เลื่อนลง" disabled={at === lessons.length - 1} onClick={() => admin.onMove(at, at + 1)}>▼</button>
                            <button
                              type="button"
                              className="curriculum-delete"
                              aria-label="เปลี่ยนชื่อบทนี้"
                              title="เปลี่ยนชื่อบทนี้"
                              onClick={() => {
                                const name = askName("ชื่อบทใหม่", l.title);
                                if (name) admin.onRename([l.id], { title: name });
                              }}
                            >
                              ✏️
                            </button>
                            <button type="button" className="curriculum-delete" aria-label="ลบบทนี้" title="ลบบทนี้" onClick={() => admin.onDelete([l.id])}>🗑</button>
                          </span>
                        )}
                        <Link to={`/courses/${course.id}/lessons/${l.id}`}>
                          <span className="curriculum-lesson__title">{l.title || "(บทยังไม่มีชื่อ)"}</span>
                          {l.isFree && <span className="curriculum-lesson__tag">ฟรี</span>}
                          {!l.published && <span className="curriculum-lesson__tag">ร่าง</span>}
                          {isDone && <span className="curriculum-lesson__done" aria-label="เรียนแล้ว">✓</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
        {admin && (
          <button type="button" className="btn-secondary curriculum-add" onClick={admin.onAdd}>
            + เพิ่มบทเรียน
          </button>
        )}
      </nav>
    </aside>
  );
}
